import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronLeft, ChevronRight, Check, Clock, Users, MapPin, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import ServiceCard from '@/components/shared/ServiceCard';
import PricingCard from '@/components/shared/PricingCard';
import BlogCard from '@/components/shared/BlogCard';
import CTABanner from '@/components/shared/CTABanner';
import { DCA_LINKS } from '@/lib/dcaConfig';
import JsonLd, { generateOrganizationSchema, generateLocalBusinessSchema } from '@/components/seo/JsonLd';
import { publicApi } from '@/lib/api';
import { useEditMode } from '@/contexts/EditModeContext';
import EditableWrapper from '@/components/editable/EditableWrapper';

const HomePage = () => {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [services, setServices] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isEditMode } = useEditMode();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [slidesRes, servicesRes, pricingRes, postsRes, settingsRes] = await Promise.all([
          publicApi.getHeroSlides(),
          publicApi.getServices(),
          publicApi.getPricing(),
          publicApi.getRecentPosts(3),
          publicApi.getSiteSettings()
        ]);
        setSlides(slidesRes.data);
        setServices(servicesRes.data);
        setPricing(pricingRes.data);
        setPosts(postsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-advance slider
  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const mainServices = services.filter(s => s.service_type === 'main');
  const subServices = services.filter(s => s.service_type === 'sub');

  const strategistFeatures = [
    'Gain Clarity',
    'Explore and Discover',
    'Overcome Barriers',
    'Achieve More',
    'Develop Skills',
    'Purpose Aligned Career'
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>SEG Career Support | Professional Career Coaching & Development Services</title>
        <meta name="description" content="Transform your career with expert coaching, resume development, interview preparation, and job search strategies. Schedule your free 20-minute consultation today." />
        <meta name="keywords" content="career coaching, career development, resume writing, interview preparation, job search, professional development, Savannah GA" />
        <meta property="og:title" content="SEG Career Support | Professional Career Coaching" />
        <meta property="og:description" content="Transform your career with expert coaching and personalized career development services." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://segcareersupport.com" />
        <link rel="canonical" href="https://segcareersupport.com" />
      </Helmet>
      
      {/* JSON-LD Structured Data */}
      <JsonLd data={generateOrganizationSchema()} />
      <JsonLd data={generateLocalBusinessSchema()} />
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Slider */}
        <section className="relative h-[600px] md:h-[700px] overflow-hidden" data-testid="hero-slider">
          {slides.map((slide, index) => (
            <EditableWrapper
              key={slide.id}
              elementType="hero_slide"
              elementId={slide.id}
              elementData={slide}
              label={`Edit: ${slide.title}`}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <div className="absolute inset-0">
                <img
                  src={slide.image_url}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-primary/80"></div>
              </div>
              <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                <div className="max-w-2xl text-white">
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6 animate-fade-in-up">
                    {slide.title}
                  </h1>
                  <p className="text-lg md:text-xl mb-8 text-white/90 animate-fade-in-up animation-delay-100">
                    {slide.description}
                  </p>
                  <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-200">
                    {(slide.buttons && slide.buttons.length > 0) ? (
                      slide.buttons.map((btn, i) => (
                        btn.action === 'internal' ? (
                          <Button
                            key={i}
                            asChild
                            size="lg"
                            variant={btn.variant === 'outline' ? 'outline' : 'default'}
                            className={btn.variant === 'outline' ? 'border-white text-white hover:bg-white/10' : 'bg-accent hover:bg-accent/90'}
                            data-testid={`hero-btn-${index}-${i}`}
                          >
                            <Link to={btn.link}>{btn.text} <ArrowRight className="w-5 h-5 ml-2" /></Link>
                          </Button>
                        ) : (
                          <Button
                            key={i}
                            size="lg"
                            variant={btn.variant === 'outline' ? 'outline' : 'default'}
                            className={btn.variant === 'outline' ? 'border-white text-white hover:bg-white/10' : 'bg-accent hover:bg-accent/90'}
                            data-testid={`hero-btn-${index}-${i}`}
                            onClick={() => window.open(btn.link, '_blank', 'noopener,noreferrer')}
                          >
                            {btn.text} <ArrowRight className="w-5 h-5 ml-2" />
                          </Button>
                        )
                      ))
                    ) : (
                      <Button
                        size="lg"
                        className="bg-accent hover:bg-accent/90"
                        data-testid="hero-get-started-btn"
                        onClick={() => window.open(DCA_LINKS.session, '_blank', 'noopener,noreferrer')}
                      >
                        {slide.button_text || 'Get Started'} <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </EditableWrapper>
          ))}

          {/* Slider Controls */}
          {slides.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                data-testid="slider-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                data-testid="slider-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              
              {/* Dots */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
                    }`}
                    data-testid={`slider-dot-${index}`}
                  />
                ))}
              </div>
            </>
          )}
        </section>

        {/* Services Preview (3 Cards with Images) */}
        <section className="py-12 bg-muted/30" data-testid="services-preview-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              {mainServices.map((service) => (
                <ServiceCard key={service.id} service={service} variant="image" />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Banner 1 */}
        <CTABanner />

        {/* Our Services (Icon Grid) */}
        <section className="py-12" data-testid="services-grid-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Our Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Learn how our Career Coaching services and packages can accelerate your career journey.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {subServices.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>

        {/* Career Strategist Section */}
        <section className="py-12 bg-muted/30" data-testid="strategist-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                  Career Strategist
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Let me help you navigate the search for a better job faster, develop your leadership potential, 
                  transition into a new career, and assist your downsized employees with their career transition. 
                  I provide professional guidance, tools and a plan so you have a clear direction and no detours to slow you down.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {strategistFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
                <Button size="lg" data-testid="strategist-contact-btn" onClick={() => window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer')}>
                  Free 20-Minute Career Strategy Session <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-primary/10 to-accent/10">
                  <img
                    src="https://customer-assets.emergentagent.com/job_7df1c564-588e-4a17-acd3-02a3087015ac/artifacts/yimtouyg__DSA0477BSC.jpg"
                    alt="David Ashley - Career Strategist"
                    className="w-full h-auto"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-primary rounded-xl"></div>
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-xl"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Workshop Spotlight */}
        <section className="py-12 bg-gradient-to-br from-accent/10 to-primary/10" data-testid="featured-workshop-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_8056591d-682d-433c-8623-ad0e4f24d171/artifacts/r2bk86co_IMG-8395.jpg"
                    alt="Featured Workshop - Resume Writing"
                    className="w-full h-auto"
                  />
                </div>
                <div className="absolute top-4 left-4 bg-accent text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                  Featured Workshop
                </div>
              </div>
              <div>
                <span className="inline-block bg-primary/10 text-primary px-4 py-1 rounded-full text-sm font-medium mb-4">
                  Professional Development
                </span>
                <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                  Resume Writing Workshop
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Learn professional resume writing techniques that get results. Our expert-led workshop covers ATS optimization, impact statements, and tailoring your resume for specific opportunities.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Clock className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm">45 min - Full Day</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm">Any Group Size</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm">Virtual or On-Site</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Award className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm">Expert Facilitation</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg">
                    <Link to="/workshop/request">
                      Request This Workshop <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/workshops">View All Workshops</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-12" data-testid="pricing-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Our Pricing</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Choose the plan that fits your career development needs
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 items-stretch">
              {pricing.map((plan) => (
                <PricingCard key={plan.id} plan={plan} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Banner 2 */}
        <CTABanner 
          title="Find out how a career strategist can help change your life."
          variant="alt"
        />

        {/* Latest News */}
        <section className="py-12" data-testid="latest-news-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Latest News</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Stay updated with our latest career tips and insights
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {posts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
            <div className="text-center mt-10">
              <Button asChild variant="outline" size="lg" data-testid="view-all-posts-btn">
                <Link to="/blog">
                  View All Posts <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
